import {
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValueText,
} from '@/components/ui/select';
import { Field } from '@/components/ui/field';
import { useUpdateUserSettingsAction } from '@/hooks/actions';
import { Box, Button, createListCollection, Flex } from '@chakra-ui/react';

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

type Language = 'en' | 'fr' | 'it';

const PreferencesSection = () => {
  const { t, i18n } = useTranslation();
  const updateSettings = useUpdateUserSettingsAction();

  // Use i18n.language as the source of truth for the current language
  // This ensures the select always shows the currently active language
  const [selectedLanguage, setSelectedLanguage] = useState<Language>(
    (i18n.language as Language) || 'en'
  );

  // Sync state with i18n language when it changes
  useEffect(() => {
    setSelectedLanguage((i18n.language as Language) || 'en');
  }, [i18n.language]);

  const languageCollection = useMemo(
    () =>
      createListCollection({
        items: [
          { value: 'en', label: 'English' },
          { value: 'fr', label: 'Français' },
          { value: 'it', label: 'Italiano' },
        ],
      }),
    []
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Use i18n.language as the source of truth since it updates immediately
    const currentLanguage = i18n.language as Language;
    if (selectedLanguage === currentLanguage) {
      return; // No change
    }

    updateSettings.mutate({ preferredLanguage: selectedLanguage });
  };

  return (
    <Box as="form" onSubmit={handleSubmit} maxW="400px" w="full">
      <Field label={t('settings.language')} helperText={t('settings.languageDescription')}>
        <SelectRoot
          collection={languageCollection}
          value={[selectedLanguage]}
          onValueChange={(e) => setSelectedLanguage((e.value[0] as Language) ?? 'en')}
          size="md"
        >
          <SelectTrigger>
            <SelectValueText />
          </SelectTrigger>
          <SelectContent>
            {languageCollection.items.map((item) => (
              <SelectItem key={item.value} item={item}>
                {item.label}
              </SelectItem>
            ))}
          </SelectContent>
        </SelectRoot>
      </Field>

      <Flex gap={2} mt={4}>
        <Button
          type="submit"
          size="sm"
          loading={updateSettings.isPending}
          loadingText={t('settings.saving')}
          disabled={selectedLanguage === (i18n.language as Language)}
        >
          {t('common.save')}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={selectedLanguage === (i18n.language as Language) || updateSettings.isPending}
          onClick={() => setSelectedLanguage((i18n.language as Language) || 'en')}
        >
          {t('common.cancel')}
        </Button>
      </Flex>
    </Box>
  );
};

export default PreferencesSection;
